import React, {
  ChangeEventHandler,
  FocusEventHandler,
  MouseEventHandler,
  useEffect,
  useRef,
  useState,
} from "react";

import {
  dbEmailExists,
  emailTakenErrorMsg,
  initialEmailErrorMsg,
  initialEmailSuggestion,
} from "./email-helpers";
import { isValidEmail } from "@utils/validations";
import { useEmailStore } from "@sp-state/useEmailStore";
import { useFormData } from "@sp-state/useFormData";
import Input from "../Input";
import InputWrapper from "../InputWrapper";

import styles from "./EmailInput.module.scss";
import { mailCheck } from "@components/SignUpForm/helpers/mailCheck";

const EmailInput = () => {
  const { email: storedEmail } = useEmailStore(state => state);
  const { email, setError, setValue, setIsValid } = useFormData(state => state);

  const [checkingEmail, setCheckingEmail] = useState(false);
  const [emailSuggestion, setEmailSuggestion] = useState(
    initialEmailSuggestion
  );
  const [emailErrorMsg, setEmailErrorMsg] = useState(initialEmailErrorMsg);

  const INPUT_NAME = "email";
  let keyUpTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!storedEmail) return;

    setValue("email", storedEmail);

    if (isValidEmail(storedEmail)) {
      dbEmailExists(storedEmail).then(emailExists => {
        if (emailExists) {
          handleEmailExists(storedEmail);
          return;
        }

        setIsValid("email", true);
      });
    } else {
      setError("email", true);
      setIsValid("email", false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onBlur: FocusEventHandler<HTMLInputElement> = e => {
    if (!isValidEmail(e.target.value)) {
      setError(INPUT_NAME, true);
      setIsValid(INPUT_NAME, false);
    }
  };

  const onChange: ChangeEventHandler<HTMLInputElement> = e => {
    const { value } = e.target;

    setError(INPUT_NAME, false);
    setIsValid(INPUT_NAME, false);
    setValue(INPUT_NAME, value);
    setEmailSuggestion(initialEmailSuggestion);
    setEmailErrorMsg(initialEmailErrorMsg);

    if (keyUpTimeout.current) clearTimeout(keyUpTimeout.current);

    if (isValidEmail(value)) {
      keyUpTimeout.current = setTimeout(async function () {
        setCheckingEmail(true);
        const emailExists = await dbEmailExists(value);
        setCheckingEmail(false);

        if (emailExists) {
          handleEmailExists(value);
          return;
        }

        mailCheck(
          value,
          (emailSuggestion: Record<string, any>) =>
            setEmailSuggestion({
              shouldShow: true,
              suggestion: emailSuggestion.full,
            }),
          () => setEmailSuggestion(initialEmailSuggestion)
        );

        setIsValid(INPUT_NAME, true);
      }, 250);
    }
  };

  const onSuggestionClick: MouseEventHandler<HTMLSpanElement> = e => {
    setValue(INPUT_NAME, (e.target as HTMLElement).outerText);
    setEmailSuggestion(initialEmailSuggestion);
  };

  const handleEmailExists = (email: string) => {
    setValue(INPUT_NAME, email);
    setError(INPUT_NAME, true);
    setIsValid(INPUT_NAME, false);
    setEmailErrorMsg(emailTakenErrorMsg);
  };

  return (
    <InputWrapper label="Email">
      <Input
        hasError={email.hasError}
        isValid={email.isValid}
        loading={checkingEmail}
        onBlur={onBlur}
        onChange={onChange}
        value={email.value}
        name="email"
        autoComplete="email"
      />
      {emailSuggestion.shouldShow && (
        <p className={styles.emailSuggestion}>
          Did you mean{" "}
          <span onClick={onSuggestionClick}>{emailSuggestion.suggestion}</span>
        </p>
      )}

      {email.hasError && !emailSuggestion.shouldShow && (
        <p
          className={styles.emailErrorMsg}
          dangerouslySetInnerHTML={{ __html: emailErrorMsg }}
        />
      )}
    </InputWrapper>
  );
};

export default EmailInput;
